import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useMeContext } from '../../app/MeContext';
import { useScreen, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { addDaysISO, formatISODay, parseHHMM, minutesToHHMM, todayISO, weekdayRu } from '../../lib/time';
import { formatKop } from '../../lib/money';
import { pluralWithCount } from '../../lib/plural';
import { Notice, Pill } from '../../ui/parts';
import { useCreateSeries, usePreviewSeries, useSchedule, useStaffChild } from '../api';
import type { SeriesPreview } from '../types';

/**
 * Серия занятий: сначала предпросмотр, потом создание. Из «вт и чт до конца
 * декабря» реально получается меньше — причины пропуска считает сервер и
 * показывает до создания, иначе расписание разойдётся с реальностью.
 */
export function SeriesEditor() {
  const me = useMeContext();
  const navigate = useNavigate();
  const toast = useToast();
  const { childId: raw } = useParams();
  const childId = Number(raw);

  const child = useStaffChild(Number.isFinite(childId) ? childId : null);
  const schedule = useSchedule();
  const preview = usePreviewSeries();
  const create = useCreateSeries();

  const rates = (schedule.data?.rates ?? []).filter((r) => r.is_active);
  const [serviceId, setServiceId] = useState<number | null>(null);
  const [weekdays, setWeekdays] = useState<number[]>([1, 3]);
  const [time, setTime] = useState('16:00');
  const [endsOn, setEndsOn] = useState(() => addDaysISO(todayISO(me.center.tz), 90));
  const [result, setResult] = useState<SeriesPreview | null>(null);

  useEffect(() => {
    if (serviceId === null && rates.length) setServiceId(rates[0].service_id);
  }, [rates, serviceId]);

  // Предпросмотр пересчитывается на каждое изменение: он и есть ответ на вопрос
  // «что получится», а не отдельный шаг, который можно пропустить.
  useEffect(() => {
    if (!childId || serviceId === null || !weekdays.length || !endsOn) {
      setResult(null);
      return;
    }
    const id = window.setTimeout(() => {
      preview.mutate(
        { childId, serviceId, weekdays: [...weekdays].sort(), timeMin: parseHHMM(time), endsOn },
        { onSuccess: setResult, onError: () => setResult(null) },
      );
    }, 250);
    return () => clearTimeout(id);
  }, [childId, serviceId, weekdays.join(','), time, endsOn]); // eslint-disable-line react-hooks/exhaustive-deps

  const ok = !!result?.willCreate;

  // В разделах с вкладками главной кнопки внизу нет — действие живёт внутри экрана,
  // как в прототипе.
  useScreen('Регулярные записи', null);

  function submit() {
    if (serviceId === null) return;
    create.mutate(
      { childId, serviceId, weekdays: [...weekdays].sort(), timeMin: parseHHMM(time), endsOn },
      {
        onSuccess: () => {
          toast('Записи созданы');
          navigate('/staff/kids');
        },
        onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось создать серию'),
      },
    );
  }

  const skipped = (result?.dates ?? []).filter((d) => d.skip);

  return (
    <Screen>
      <div>
        <p className="eyebrow">{child.data?.child.name ?? 'Ребёнок'}</p>
        <p className="lead" style={{ marginTop: 6 }}>
          Записи создадутся сразу на весь период. Даты, которые не попадают в рабочие часы
          или в отпуск, пропустим.
        </p>
      </div>

      <div className="field">
        <label htmlFor="srSvc">Услуга</label>
        <select id="srSvc" value={serviceId ?? ''} onChange={(e) => setServiceId(Number(e.target.value))}>
          {rates.length ? (
            rates.map((r) => (
              <option key={r.service_id} value={r.service_id}>
                {r.title} · {r.duration_min} мин · {formatKop(r.price)}
              </option>
            ))
          ) : (
            <option value="">У вас нет услуг — их назначает администратор</option>
          )}
        </select>
      </div>

      <p className="eyebrow" style={{ marginTop: 4 }}>Дни недели</p>
      <div className="chips">
        {[0, 1, 2, 3, 4, 5, 6].map((d) => (
          <button
            key={d}
            className="chip"
            aria-pressed={weekdays.includes(d)}
            onClick={() => setWeekdays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]))}
          >
            {weekdayRu(d)}
          </button>
        ))}
      </div>

      <div className="two">
        <div className="field">
          <label htmlFor="srTime">Время</label>
          <input id="srTime" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="srTo">По какое число</label>
          <input id="srTo" type="date" value={endsOn} min={todayISO(me.center.tz)} onChange={(e) => setEndsOn(e.target.value)} />
        </div>
      </div>

      <div className="sechead" style={{ marginTop: 4 }}>
        <p className="eyebrow">Что получится</p>
        <span className="lead" style={{ fontSize: 13 }}>
          {result
            ? `${pluralWithCount(result.willCreate, 'запись', 'записи', 'записей')}${result.skipped ? ` · ${result.skipped} пропустим` : ''}`
            : ''}
        </span>
      </div>

      <div className="rows">
        {result?.dates.length ? (
          result.dates.slice(0, 8).map((d) => (
            <div className="rw" key={d.date}>
              <span className="rw-body">
                <b>{formatISODay(d.date)}</b>
                <span>{minutesToHHMM(parseHHMM(time))}{d.skip ? ` · ${d.skip}` : ''}</span>
              </span>
              {d.skip ? <Pill kind="off">пропуск</Pill> : <Pill kind="paid">создадим</Pill>}
            </div>
          ))
        ) : (
          <p className="lead">
            {weekdays.length ? 'Считаем даты…' : 'Выберите хотя бы один день недели.'}
          </p>
        )}
      </div>

      {result && result.dates.length > 8 ? (
        <p className="footnote" style={{ textAlign: 'left' }}>
          …и ещё {result.dates.length - 8} по тому же правилу
        </p>
      ) : null}

      {skipped.length ? (
        <Notice>
          {pluralWithCount(skipped.length, 'дата не подходит', 'даты не подходят', 'дат не подходят')}: {skipped[0].skip} и
          т. п. Эти записи не создадим — остальные встанут в расписание.
        </Notice>
      ) : null}

      <button className="btn solid" disabled={!ok || create.isPending} onClick={() => submit()}>
        {create.isPending ? 'Создаём…' : 'Создать записи'}
      </button>
    </Screen>
  );
}
